import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Send, Bot, User, Sparkles, ArrowRight, MessageSquare,
  MapPin, Building2, Lock, Zap,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TypingDots() {
  return (
    <div className="flex gap-1 items-center px-3 py-2">
      {[0, 1, 2].map(i => (
        <div key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
      ))}
    </div>
  );
}

function IntentBadge({ intent }) {
  const map = {
    buyer_inquiry: { label: 'Buyer Lead', color: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
    seller_inquiry: { label: 'Seller Lead', color: 'bg-violet-500/15 text-violet-400 border-violet-500/25' },
    showing_request: { label: 'Showing Request', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
    appointment_request: { label: 'Appointment', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
    listing_question: { label: 'Listing Inquiry', color: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
    property_search: { label: 'Property Search', color: 'bg-sky-500/15 text-sky-400 border-sky-500/25' },
  };
  const m = map[intent] || { label: intent?.replace(/_/g, ' '), color: 'bg-secondary text-muted-foreground border-border' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${m.color}`}>{m.label}</span>;
}

export default function DemoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [lastIntent, setLastIntent] = useState('');
  const [lastEntities, setLastEntities] = useState({});
  const chatEndRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/growth/demo/${slug}`)
      .then(({ data }) => {
        setAgent(data.agent);
        setAssistant(data.assistant);
        setSession(data.session);
      })
      .catch(() => setError('Agent not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending || !session) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/growth/demo/${slug}/chat`, {
        session_id: session.id,
        message: msg,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response_text }]);
      setSession(data.session);
      setLastIntent(data.detected_intent);
      setLastEntities(data.extracted_entities || {});
    } catch (err) {
      if (err.response?.status === 429) {
        setMessages(prev => [...prev, { role: 'system', content: err.response.data.detail }]);
      } else {
        setMessages(prev => [...prev, { role: 'system', content: 'Something went wrong. Please try again.' }]);
      }
    } finally {
      setSending(false);
    }
  };

  const limitReached = session?.limit_reached || session?.remaining === 0;
  const capturedFields = Object.entries(lastEntities).filter(([, v]) => v && v !== 'null');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-foreground mb-2">Demo Not Available</h1>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <Button onClick={() => navigate('/signup')} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="demo-signup-btn">
            Create Your Own AI Assistant
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="demo-page">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Bot size={18} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                AI Assistant for {agent?.full_name}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {agent?.brokerage_name} {agent?.display_market ? `\u00B7 ${agent.display_market}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">Powered by Agent9</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col" style={{ minHeight: 'calc(100vh - 60px)' }}>
        {/* Info banner */}
        <div className="mb-4 px-3 py-2 rounded-lg bg-card border border-border text-center" data-testid="demo-info-banner">
          <p className="text-xs text-muted-foreground">
            You're interacting with an AI assistant for <span className="text-foreground font-medium">{agent?.full_name}</span>.
            This is a demo with {session?.max_interactions || 2} free interactions.
          </p>
        </div>

        {/* Chat area */}
        <Card className="flex-1 border-border bg-card/80 mb-4 overflow-hidden flex flex-col">
          <CardContent className="flex-1 p-4 overflow-y-auto space-y-3" style={{ maxHeight: '450px' }} data-testid="demo-chat-area">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12 gap-3">
                <div className="h-16 w-16 rounded-2xl bg-emerald-500/8 border border-emerald-500/15 flex items-center justify-center">
                  <MessageSquare size={28} className="text-emerald-500/40" />
                </div>
                <h3 className="text-sm font-medium text-foreground">Start a conversation</h3>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Try asking about buying or selling a home, scheduling a showing, or any real estate question.
                </p>
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                  {[
                    "I'm looking to buy a home under $500K",
                    "I want to sell my house",
                    "Can I schedule a showing?",
                  ].map(s => (
                    <button key={s} onClick={() => { setInput(s); }}
                      className="text-[10px] px-3 py-1.5 rounded-full bg-muted border border-border text-foreground hover:text-foreground hover:border-emerald-500/30 transition-all"
                      data-testid="demo-suggestion">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m.role + "-" + i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role !== 'user' && (
                  <div className="h-7 w-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot size={14} className="text-emerald-400" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-emerald-500/15 text-foreground border border-emerald-500/20'
                    : m.role === 'system'
                    ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                    : 'bg-muted text-foreground border border-border'
                }`} data-testid={`demo-msg-${m.role}`}>
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="h-7 w-7 rounded-lg bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User size={14} className="text-foreground" />
                  </div>
                )}
              </div>
            ))}

            {sending && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Bot size={14} className="text-emerald-400" />
                </div>
                <div className="bg-muted border border-border rounded-xl">
                  <TypingDots />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </CardContent>

          {/* Input */}
          <div className="border-t border-border p-3">
            {limitReached ? (
              <div className="text-center py-2">
                <p className="text-xs text-muted-foreground mb-3">Demo limit reached. Want unlimited access?</p>
                <div className="flex gap-2 justify-center">
                  <Button size="sm" onClick={() => navigate('/signup')}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 text-xs px-4 gap-1.5"
                    data-testid="demo-cta-signup">
                    <Zap size={12} /> Create Your Own AI <ArrowRight size={12} />
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-9 rounded-lg bg-muted border border-border text-foreground text-sm px-3 focus:outline-none focus:border-emerald-500/50 placeholder:text-muted-foreground"
                  disabled={sending}
                  data-testid="demo-chat-input"
                />
                <Button type="submit" size="sm" disabled={sending || !input.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 w-9 p-0"
                  data-testid="demo-send-btn">
                  <Send size={14} />
                </Button>
              </form>
            )}
            {session && !limitReached && (
              <p className="text-[10px] text-muted-foreground text-center mt-1.5">
                {session.remaining} interaction{session.remaining !== 1 ? 's' : ''} remaining
              </p>
            )}
          </div>
        </Card>

        {/* Post-interaction recap */}
        {messages.length > 0 && lastIntent && (
          <Card className="border-border bg-card/80 mb-4" data-testid="demo-recap">
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <Sparkles size={12} className="text-emerald-400" /> What the AI detected
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <IntentBadge intent={lastIntent} />
              </div>
              {capturedFields.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Captured Info</p>
                  <div className="flex flex-wrap gap-1.5">
                    {capturedFields.slice(0, 8).map(([k, v]) => (
                      <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-muted border border-border text-foreground">
                        {k.replace(/_/g, ' ')}: <span className="text-foreground font-medium">{String(v)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="demo-cta-section">
          <button onClick={() => navigate('/signup')}
            className="group p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all text-left"
            data-testid="demo-cta-create">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap size={16} className="text-emerald-400" />
              <span className="text-sm font-semibold text-foreground">Create your own AI assistant</span>
            </div>
            <p className="text-xs text-muted-foreground">Get a dedicated AI phone number that answers calls, qualifies leads, and books appointments 24/7.</p>
            <span className="text-emerald-400 text-xs mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Start free trial <ArrowRight size={12} />
            </span>
          </button>
          <button onClick={() => navigate('/signup')}
            className="group p-4 rounded-xl bg-gradient-to-br from-sky-500/10 to-sky-600/5 border border-sky-500/20 hover:border-sky-500/40 transition-all text-left"
            data-testid="demo-cta-trial">
            <div className="flex items-center gap-2 mb-1.5">
              <Building2 size={16} className="text-sky-400" />
              <span className="text-sm font-semibold text-foreground">7-day free trial</span>
            </div>
            <p className="text-xs text-muted-foreground">No credit card required. Set up in 5 minutes. Cancel anytime.</p>
            <span className="text-sky-400 text-xs mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Get started <ArrowRight size={12} />
            </span>
          </button>
        </div>

        {/* Footer */}
        <div className="text-center pb-4">
          <p className="text-[10px] text-muted-foreground">
            Powered by <span className="text-muted-foreground font-medium">Agent9</span> &mdash; AI assistants for real estate professionals
          </p>
        </div>
      </div>
    </div>
  );
}
